import { Tooltip } from 'bootstrap';

import { ComboBox, ChangeEventArgs as ComboBoxChangeEventArgs } from '@syncfusion/ej2-dropdowns';

import { routes } from '@/shared/routes';

import { listen } from '@/front/event';
import { GET } from '@/front/fetch';
import { confirmModal } from '@/front/modal';
import {
  hasSubTaskItems,
  replaceSubTasks,
  updateChecklistDeleteAllVisibility,
  updatePersistSubTasksVisibility,
} from '@/front/subTask';

type ChecklistTemplateOption = {
  id: string;
  name: string;
  items: { task: string; depth: 0 | 1 }[];
};

let cachedTemplates: ChecklistTemplateOption[] | null = null;

const comboboxByPicker = new WeakMap<HTMLInputElement, ComboBox>();

const clearTemplatePicker = (card: Element) => {
  const picker = card.querySelector('input[data-role="checklist-template-picker"]') as HTMLInputElement | null;

  if (!picker) {
    return;
  }

  const combobox = comboboxByPicker.get(picker);

  if (!combobox) {
    return;
  }

  picker.dataset.silentChange = 'true';
  combobox.value = null;
  combobox.text = null;
  window.setTimeout(() => {
    picker.dataset.silentChange = 'false';
  }, 0);
};

const loadChecklistTemplates = async (): Promise<ChecklistTemplateOption[]> => {
  if (cachedTemplates) {
    return cachedTemplates;
  }

  const res = await GET(routes.checklistTemplates.options());

  if (!res.ok) {
    cachedTemplates = [];
    return cachedTemplates;
  }

  const body = (await res.json()) as { result?: ChecklistTemplateOption[] };
  cachedTemplates = body.result ?? [];

  return cachedTemplates;
};

const hidePicker = (picker: HTMLInputElement) => {
  picker.style.display = 'none';
};

const initChecklistDeleteAllButtons = () => {
  [...document.querySelectorAll('table.sub-tasks')].forEach((table) => {
    updateChecklistDeleteAllVisibility(table as HTMLTableElement);
  });

  [...document.querySelectorAll('a[data-role="checklist-delete-all"]')]
    .map((button) => button as HTMLAnchorElement)
    .forEach((button) => {
      if (button.dataset.initialized === 'true') {
        return;
      }

      button.dataset.initialized = 'true';

      const tooltip = Tooltip.getOrCreateInstance(button);
      button.addEventListener('click', () => {
        tooltip.hide();
        button.blur();
      });

      button.addEventListener('click', async (e) => {
        e.preventDefault();

        if (button.dataset.disabled === 'true') {
          return;
        }

        const card = button.closest('.card');
        const table = card?.querySelector('table.sub-tasks') as HTMLTableElement | null;

        if (!table || !hasSubTaskItems(table)) {
          return;
        }

        const confirmed = await confirmModal(
          'Are you sure you want to delete the checklist for this task?',
          'Delete',
          'danger',
        );

        if (confirmed) {
          await replaceSubTasks(table, []);
          if (card) {
            clearTemplatePicker(card);
          }
        }
      });
    });
};

const initChecklistTemplatePickers = async () => {
  initChecklistDeleteAllButtons();

  const templates = await loadChecklistTemplates();
  const templatesById = Object.fromEntries(templates.map((template) => [template.id, template]));

  [...document.querySelectorAll('input[data-role="checklist-template-picker"]')]
    .map((picker) => picker as HTMLInputElement)
    .forEach((picker) => {
      if (picker.dataset.initialized === 'true') {
        return;
      }

      picker.dataset.initialized = 'true';

      if (!templates.length) {
        hidePicker(picker);
        return;
      }

      const templateDataSource = templates.map((template) => ({
        text: template.name,
        value: template.id,
      }));

      const combobox = new ComboBox({
        dataSource: templateDataSource,
        fields: {
          text: 'text',
          value: 'value',
        },
        width: '240px',
        placeholder: 'Choose template...',
        allowCustom: false,
        allowFiltering: true,
        showClearButton: false,
        popupHeight: 350,
        change: async (args: ComboBoxChangeEventArgs) => {
          if (args.isInteracted === false) {
            return;
          }

          if (picker.dataset.silentChange === 'true') {
            return;
          }

          const selectedTemplateId = String(args.value ?? '');

          const card = picker.closest('.card');
          const table = card?.querySelector('table.sub-tasks') as HTMLTableElement | null;

          if (!table || !selectedTemplateId) {
            return;
          }

          const template = templatesById[selectedTemplateId];

          if (!template) {
            return;
          }

          if (hasSubTaskItems(table)) {
            const confirmed = await confirmModal(
              'This will replace your existing checklist items. Do you want to continue?',
              'Replace',
              'warning',
            );

            if (!confirmed) {
              picker.dataset.silentChange = 'true';
              combobox.value = null;
              window.setTimeout(() => {
                picker.dataset.silentChange = 'false';
              }, 0);
              return;
            }
          }

          await replaceSubTasks(table, template.items);
          updatePersistSubTasksVisibility(table);
        },
      });

      combobox.appendTo(picker);
      comboboxByPicker.set(picker, combobox);

      const wrapper = picker.closest('.e-control-wrapper') ?? picker.parentElement;
      const hiddenSubmitField = wrapper?.querySelector<HTMLInputElement | HTMLSelectElement>(
        'input.e-ddl-hidden, select.e-ddl-hidden',
      );
      hiddenSubmitField?.removeAttribute('name');

      picker.setAttribute('aria-label', 'Choose checklist template');
      picker.setAttribute('title', 'Choose checklist template');
    });
};

document.addEventListener('DOMContentLoaded', () => {
  initChecklistTemplatePickers();
});
listen('editablesReady', () => {
  initChecklistTemplatePickers();
});
